<template>
    <div class="searchable-select">
      <div class="selected-item" @click="toggleDropdown">
        {{ selectedItem?.label || 'Select an item' }}
        <span class="dropdown-arrow">{{ isOpen ? '▲' : '▼' }}</span>
      </div>
  
      <div v-if="isOpen" class="dropdown-content">
        <input
          type="text"
          v-model="searchQuery"
          placeholder="Search..."
          class="search-input"
        />
        <ul class="item-list">
          <li
            v-for="item in filteredItems"
            :key="item.id"
            @click="selectItem(item)"
            class="item"
          >
            {{ item.label }}
          </li>
          <li v-if="filteredItems.length === 0" class="no-results">
            No results found.
          </li>
        </ul>
      </div>
    </div>
  </template>
  
  <script>
  export default {
    props: {
      items: { // This prop will receive your sortedMetrics data
        type: Array,
        default: () => []
      },
      initialSelectionId: {
        type: [String, Number],
        default: null
      }
    },
    data() {
      return {
        isOpen: false,
        searchQuery: '',
        selectedItem: null,
        internalItems: [] // A local copy to work with filtering
      };
    },
    computed: {
      filteredItems() {
        if (!this.searchQuery) {
          return this.internalItems;
        }
        const query = this.searchQuery.toLowerCase();
        return this.internalItems.filter(item =>
          item.label.toLowerCase().includes(query)
        );
      }
    },
    watch: {
      // Watch for changes in the 'items' prop and update internalItems
      items: {
        immediate: true, // Run handler immediately on component mount
        handler(newItems) {
          this.internalItems = [...newItems];
          // If initialSelectionId is set, find and set the selected item
          if (this.initialSelectionId !== null) {
            this.selectedItem = this.internalItems.find(item => item.id === this.initialSelectionId);
          } else {
              this.selectedItem = null; // Clear selection if initialId becomes null
          }
        }
      },
      // Watch for changes in initialSelectionId to update the displayed item
      initialSelectionId(newId) {
          if (newId !== null) {
              this.selectedItem = this.internalItems.find(item => item.id === newId);
          } else {
              this.selectedItem = null;
          }
      },
      selectedItem(newItem) {
        // Emit the selected item's ID when it changes internally
        this.$emit('update:selected-id', newItem ? newItem.id : null);
      }
    },
    methods: {
      toggleDropdown() {
        this.isOpen = !this.isOpen;
        if (this.isOpen) {
          this.$nextTick(() => {
            this.$el.querySelector('.search-input')?.focus(); // Focus input after dropdown opens
          });
        }
      },
      selectItem(item) {
        this.selectedItem = item;
        this.isOpen = false;
        this.searchQuery = ''; // Clear search query after selection
      },
      // handleClickOutside method remains the same for closing dropdown on outside clicks
      handleClickOutside(event) {
        if (this.$el && !this.$el.contains(event.target)) {
          this.isOpen = false;
          this.searchQuery = '';
        }
      }
    },
    mounted() {
      document.addEventListener('click', this.handleClickOutside);
    },
    beforeUnmount() {
      document.removeEventListener('click', this.handleClickOutside);
    }
  };
  </script>
  
  <style scoped>
  /* Styles from the previous example for .searchable-select, .selected-item, etc. */
  .searchable-select {
    position: relative;
    width: 300px;
    border: 1px solid #ccc;
    border-radius: 4px;
    font-family: sans-serif;
  }
  
  .selected-item {
    padding: 10px;
    cursor: pointer;
    background-color: #f9f9f9;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  
  .dropdown-arrow {
    margin-left: 10px;
  }
  
  .dropdown-content {
    position: absolute;
    top: 100%; /* Position below the selected item */
    left: 0;
    right: 0;
    border: 1px solid #ccc;
    border-top: none;
    background-color: white;
    z-index: 1000;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    max-height: 200px;
    overflow-y: auto;
  }
  
  .search-input {
    width: calc(100% - 20px);
    padding: 8px 10px;
    margin: 10px;
    border: 1px solid #ddd;
    border-radius: 3px;
    box-sizing: border-box;
  }
  
  .item-list {
    list-style: none;
    padding: 0;
    margin: 0;
  }
  
  .item {
    padding: 10px;
    cursor: pointer;
  }
  
  .item:hover {
    background-color: #f0f0f0;
  }
  
  .no-results {
    padding: 10px;
    color: #888;
    text-align: center;
  }
  </style>